/* Amounts and percentages are server formatted. No client-side discount arithmetic. */
(() => {
  if (window.fadlashPriceGroupsBound) return;
  window.fadlashPriceGroupsBound = true;
  const offerSelector = '[data-fadlash-anniversary-price], [data-fadlash-anniversary-expiry]';
  const expireOffers = () => {
    const now = Date.now();
    let next = 2147483647;
    document.querySelectorAll(offerSelector).forEach((offer) => {
      const expires = Date.parse(offer.dataset.expires);
      if (!Number.isFinite(expires) || expires <= now) offer.hidden = true;
      else next = Math.min(next, expires - now);
    });
    clearTimeout(window.fadlashPriceExpiryTimer);
    window.fadlashPriceExpiryTimer = setTimeout(expireOffers, Math.max(1, next));
  };
  // Subscription apps may update the purchase form without replacing native price HTML.
  // Preserve the previous eligibility state, then restore it only for a one-time purchase.
  const subscriptionHidden = new WeakMap();
  const syncSubscriptionOffers = () => {
    document.querySelectorAll('product-info').forEach((context) => {
      if (!context.querySelectorAll) return;
      const selected = Array.from(context.querySelectorAll('[name="selling_plan"]')).some((control) =>
        !control.disabled && String(control.value || '').trim() !== '' &&
        (!['radio', 'checkbox'].includes(control.type) || control.checked)
      );
      context.querySelectorAll('[data-fadlash-anniversary-price], .fa-anniversary-note[data-fadlash-anniversary-expiry]').forEach((offer) => {
        if (selected) {
          if (!subscriptionHidden.has(offer)) subscriptionHidden.set(offer, offer.hidden);
          offer.hidden = true;
        } else if (subscriptionHidden.has(offer)) {
          const expires = Date.parse(offer.dataset.expires);
          offer.hidden = subscriptionHidden.get(offer) || !Number.isFinite(expires) || expires <= Date.now();
          subscriptionHidden.delete(offer);
        }
      });
    });
  };
  let subscriptionSyncQueued = false;
  const queueSubscriptionSync = () => {
    if (subscriptionSyncQueued) return;
    subscriptionSyncQueued = true;
    queueMicrotask(() => { subscriptionSyncQueued = false; syncSubscriptionOffers(); });
  };
  // Eligibility comes from the same explicit manifest as the Liquid/backend scope.
  // Only quantities are counted here. All displayed monetary strings come from Liquid.
  const eligibility = window.fadlashAnniversaryEligibility;
  const proPairs = new Set(Array.isArray(eligibility?.proPairs) ? eligibility.proPairs : []);
  let cartKnown = false;
  let proQuantity = 0;
  let cartRevision = 0;
  let cartRead = null;
  let cartReadNeeded = false;
  const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const completeCart = (cart) => Boolean(cart && Array.isArray(cart.items)
    && Number.isSafeInteger(cart.item_count) && cart.item_count >= 0
    && Number.isSafeInteger(cart.total_price) && cart.total_price >= 0
    && cart.items.every((item) => item.product_id && (item.variant_id || item.id)
      && Number.isSafeInteger(item.quantity) && item.quantity >= 0
      && Number.isSafeInteger(item.final_line_price) && item.final_line_price >= 0)
    && cart.items.reduce((total, item) => total + item.quantity, 0) === cart.item_count);
  const countPro = (cart) => cart.items.reduce((total, item) => {
    const pair = String(item.product_id) + ':' + String(item.variant_id || item.id);
    return total + (proPairs.has(pair) && !item.selling_plan_allocation && !item.gift_card
      && item.final_line_price > 0 ? item.quantity : 0);
  }, 0);
  const renderOffer = (offer) => {
    if (!offer?.querySelector || offer.dataset.fadlashRate === undefined) return;
    const data = offer.dataset;
    const rate = data.fadlashRate || '';
    const pro = rate === 'pro';
    const tier = pro ? (proQuantity >= 10 ? '35' : proQuantity >= 5 ? '30' : '25') : rate;
    const quoted = pro ? (cartKnown ? data['fadlashTier' + tier] : '') : data.fadlashFinal;
    const numeric = Boolean(rate && quoted && data.fadlashBase);
    const eligibilityHidden = !rate;
    if (subscriptionHidden.has(offer)) subscriptionHidden.set(offer, eligibilityHidden);
    offer.hidden = eligibilityHidden;
    const table = offer.querySelector('[data-fadlash-pro-price]');
    const group = offer.querySelector('[data-fadlash-fixed-price]');
    const final = offer.querySelector('[data-fadlash-anniversary-final]');
    const base = offer.querySelector('[data-fadlash-anniversary-base]');
    const count = offer.querySelector('[data-fadlash-pro-quantity]');
    if (table) table.hidden = !pro;
    if (group) group.hidden = !rate || (pro && !numeric);
    if (final) { setText(final, numeric ? quoted : ''); final.hidden = !numeric; }
    if (base) { setText(base, numeric ? data.fadlashBase : ''); base.hidden = !numeric; }
    setText(offer.querySelector('[data-fadlash-anniversary-rate]'), rate && (!pro || cartKnown) ? tier + '% OFF' : '');
    setText(offer.querySelector('[data-fadlash-anniversary-label]'), numeric ? 'Anniversary price estimate' : 'Anniversary offer · selected eligible option');
    if (count) {
      count.hidden = !pro || !numeric;
      setText(count, proQuantity === 0 ? '25% for 1–4 eligible pro boxes. Your cart currently has 0 eligible boxes.'
        : `Based on ${proQuantity} eligible pro ${proQuantity === 1 ? 'box' : 'boxes'} currently in your cart.`);
    }
  };
  const renderOffers = () => {
    document.querySelectorAll('[data-fadlash-anniversary-price]').forEach(renderOffer);
    expireOffers();
    syncSubscriptionOffers();
  };
  const acceptCart = (cart) => {
    if (!proPairs.size || !completeCart(cart)) return false;
    proQuantity = countPro(cart);
    cartKnown = true;
    renderOffers();
    return true;
  };
  const refreshCart = () => {
    if (!proPairs.size || typeof fetch !== 'function' || Date.now() >= Date.parse(eligibility.end)) return Promise.resolve();
    cartReadNeeded = true;
    if (cartRead) return cartRead;
    cartRead = Promise.resolve().then(async () => {
      while (cartReadNeeded) {
        cartReadNeeded = false;
        const requestedRevision = cartRevision;
        try {
          const cartURL = typeof routes !== 'undefined' && routes.cart_url
            ? routes.cart_url + '.js' : (window.Shopify?.routes?.root || '/') + 'cart.js';
          const response = await fetch(cartURL, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!response.ok) throw new Error('Cart state unavailable');
          const cart = await response.json();
          // A newer complete event wins over an older in-flight cart read.
          if (requestedRevision === cartRevision && !acceptCart(cart)) throw new Error('Incomplete cart state');
        } catch (_) {
          if (requestedRevision === cartRevision) { cartKnown = false; renderOffers(); }
        }
      }
    }).finally(() => { cartRead = null; if (cartReadNeeded) refreshCart(); });
    return cartRead;
  };
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      cartRevision++;
      if (acceptCart(event?.cartData)) { cartReadNeeded = false; return; }
      cartKnown = false;
      renderOffers();
      refreshCart();
    });
  }
  const update = (group, option) => {
    if (!group || !option?.dataset.fadlashMoney) return;
    const data = option.dataset;
    const price = group.querySelector('[data-fadlash-price]');
    if (!price) return;
    price.classList.toggle('price--on-sale', Boolean(data.fadlashCompare));
    price.querySelectorAll('[data-fadlash-selling-price]').forEach((node) => { node.textContent = data.fadlashMoney; });
    const compare = price.querySelector('[data-fadlash-compare-price]');
    const percent = price.querySelector('[data-fadlash-saving-percent]');
    const basis = price.querySelector('[data-fadlash-compare-basis]');
    if (compare) compare.textContent = data.fadlashCompare || '';
    if (percent) percent.textContent = data.fadlashPercent || '';
    if (basis) basis.hidden = true;
    const offer = price.querySelector('[data-fadlash-anniversary-price]');
    if (offer) {
      ['fadlashRate', 'fadlashBase', 'fadlashFinal', 'fadlashTier25', 'fadlashTier30', 'fadlashTier35'].forEach((key) => {
        offer.dataset[key] = option.disabled && key !== 'fadlashRate' ? '' : data[key] || '';
      });
      renderOffer(offer);
    }
    expireOffers();
    syncSubscriptionOffers();
  };
  // Capture runs before the native cart's stopPropagation handler. Never mutates the cart.
  document.addEventListener('change', (event) => {
    const selector = event.target;
    queueSubscriptionSync();
    if (selector.matches('.fa-restock-form select[name="id"]')) {
      update(selector.closest('.fa-card').querySelector('[data-fa-price-group]'), selector.selectedOptions[0]);
    } else if (selector.matches('select[data-fcn-variant]')) {
      update(selector.closest('[data-fcn-recommendation]').querySelector('[data-fcn-price]'), selector.selectedOptions[0]);
    }
  }, true);
  document.addEventListener('input', queueSubscriptionSync, true);
  document.addEventListener('submit', syncSubscriptionOffers, true);
  document.addEventListener('shopify:section:load', renderOffers);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && proPairs.size) cartKnown = false;
    renderOffers();
    if (!document.hidden) { cartRevision++; refreshCart(); }
  });
  // Cached search/quick-view HTML and late section responses can arrive after expiry.
  // Inspect inserted price groups only; never observe our own hidden-attribute updates.
  if (typeof MutationObserver !== 'undefined') {
    let expiryQueued = false;
    new MutationObserver((records) => {
      queueSubscriptionSync();
      const hasInsertedOffer = records.some((record) => Array.from(record.addedNodes).some((node) =>
        node.nodeType === 1 && (node.matches(offerSelector) || node.querySelector(offerSelector))
      ));
      if (!hasInsertedOffer || expiryQueued) return;
      expiryQueued = true;
      queueMicrotask(() => { expiryQueued = false; renderOffers(); });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  let initialCart;
  try { initialCart = JSON.parse(document.getElementById?.('fadlash-anniversary-cart-state')?.textContent || 'null'); } catch (_) { /* Invalid boot state falls back to one cart read. */ }
  if (!acceptCart(initialCart)) { renderOffers(); if (proPairs.size) refreshCart(); }
})();
